import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useActivityStore } from '@/store/activityStore';
import { useToast } from '@/components/ui/use-toast';
import { Activity, ActivityFormData, ACTIVITY_TYPES } from '@/types/crm';

interface ActivityFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activity?: Activity | null;
  entityType?: 'lead' | 'company' | 'contact' | 'deal';
  entityId?: string;
}

export function ActivityFormDialog({ open, onOpenChange, activity, entityType, entityId }: ActivityFormDialogProps) {
  const { createActivity, updateActivity, isLoading } = useActivityStore();
  const { toast } = useToast();
  const isEditing = !!activity;

  const [formData, setFormData] = useState<ActivityFormData>({
    type: 'task',
    subject: '',
    description: '',
    due_date: '',
    lead_id: undefined,
    company_id: undefined,
    contact_id: undefined,
    deal_id: undefined,
  });

  useEffect(() => {
    if (activity) {
      setFormData({
        type: activity.type,
        subject: activity.subject,
        description: activity.description || '',
        due_date: activity.due_date ? activity.due_date.split('T')[0] : '',
        lead_id: activity.lead_id,
        company_id: activity.company_id,
        contact_id: activity.contact_id,
        deal_id: activity.deal_id,
      });
    } else {
      setFormData({
        type: 'task',
        subject: '',
        description: '',
        due_date: '',
        lead_id: entityType === 'lead' ? entityId : undefined,
        company_id: entityType === 'company' ? entityId : undefined,
        contact_id: entityType === 'contact' ? entityId : undefined,
        deal_id: entityType === 'deal' ? entityId : undefined,
      });
    }
  }, [activity, open, entityType, entityId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const submitData = {
        ...formData,
        due_date: formData.due_date || undefined,
      };

      if (isEditing && activity) {
        await updateActivity(activity.id, submitData);
        toast({ title: 'Activity updated successfully' });
      } else {
        await createActivity(submitData as ActivityFormData);
        toast({ title: 'Activity created successfully' });
      }
      onOpenChange(false);
    } catch (error) {
      toast({ title: 'Error', description: String(error), variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Activity' : 'New Activity'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Type *</Label>
            <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value as ActivityFormData['type'] })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACTIVITY_TYPES.map((type) => (
                  <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="subject">Subject *</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="due_date">Due Date</Label>
            <Input
              id="due_date"
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isLoading}>{isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
